import React, {useEffect, useMemo, useState} from "react";
import {useNavigate, useOutletContext, useParams, useSearchParams} from "react-router-dom";
import Button from "../../../../../components/Button";
import api from "../../../../../lib/api";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import {useBusiness} from "../../../../../context/BusinessContext";
import {PencilIcon, PlusIcon, SaveIcon, Trash2Icon, XIcon} from "lucide-react";
import SectionHeading from "../../../../../components/SectionHeading";
import {TicketScopePicker, type TicketKind, type TicketOption} from "../components/TicketScopePicker";

type OutletCtx = { eventId: string | null };

type DiscountType = "PERCENT" | "FIXED";

type CouponDto = {
    id: string;
    enabled: boolean;
    code: string;

    discountType: DiscountType;
    discountValue: number;
    currency?: string | null;

    maxTotalUses?: number | null;        // null => unlimited
    maxUsesPerCustomer?: number | null;  // null => unlimited
    usedCount?: number | null;

    validFrom?: string | null;   // ISO
    validUntil?: string | null;  // ISO

    minOrderAmount?: number | null;

    ticketTypeIds?: string[] | null; // empty or null => all
    description?: string | null;
    createdAt?: string | null;
};

type CouponForm = {
    enabled: boolean;
    code: string;

    discountType: DiscountType;
    discountValue: string;
    currency: string;

    maxTotalUses: string;        // "" means unlimited (or 0 if you prefer)
    maxUsesPerCustomer: string;

    validFrom: string;           // datetime-local
    validUntil: string;

    minOrderAmount: string;

    ticketTypeIds: string[];     // empty => all ticket types
    description: string;
};

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(" ");

function toNumber(v: string): number {
    const n = Number((v ?? "").replace(/[^\d.]/g, ""));
    return Number.isFinite(n) ? n : 0;
}

function toIntOrNull(v: string): number | null {
    const s = (v ?? "").trim();
    if (!s) return null; // treat empty as unlimited
    const n = Number(s.replace(/[^\d]/g, ""));
    if (!Number.isFinite(n)) return null;
    return Math.max(0, Math.trunc(n));
}

function normalizeCode(code: string) {
    return (code ?? "")
        .toUpperCase()
        .replace(/\s+/g, "")
        .replace(/[^A-Z0-9_-]/g, "");
}

function toLocalInputValue(iso?: string | null) {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(v?: string | null) {
    if (!v) return null;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
}

function formatWindow(c: CouponDto) {
    const a = c.validFrom ? new Date(c.validFrom) : null;
    const b = c.validUntil ? new Date(c.validUntil) : null;
    const fmt = (d: Date) =>
        d.toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit"
        });
    if (!a && !b) return "No time limit";
    if (a && !b) return `From ${fmt(a)}`;
    if (!a && b) return `Until ${fmt(b)}`;
    if (a && b) return `${fmt(a)} → ${fmt(b)}`;
    return "—";
}

function formatDiscount(c: CouponDto) {
    if (c.discountType === "PERCENT") return `${Math.round(c.discountValue)}%`;
    const cur = c.currency || "NAD";
    return `${cur} ${Number(c.discountValue || 0).toFixed(2)}`;
}

function isExpired(c: CouponDto) {
    if (!c.validUntil) return false;
    const t = new Date(c.validUntil).getTime();
    return Number.isFinite(t) ? t < Date.now() : false;
}

function isNotYetActive(c: CouponDto) {
    if (!c.validFrom) return false;
    const t = new Date(c.validFrom).getTime();
    return Number.isFinite(t) ? t > Date.now() : false;
}

function emptyForm(): CouponForm {
    return {
        enabled: true,
        code: "",
        discountType: "PERCENT",
        discountValue: "10",
        currency: "NAD",
        maxTotalUses: "",
        maxUsesPerCustomer: "1",
        validFrom: "",
        validUntil: "",
        minOrderAmount: "",
        ticketTypeIds: [],
        description: "",
    };
}

function formFromDto(d: CouponDto): CouponForm {
    return {
        enabled: Boolean(d.enabled),
        code: d.code ?? "",
        discountType: d.discountType === "FIXED" ? "FIXED" : "PERCENT",
        discountValue: String(d.discountValue ?? 0),
        currency: String(d.currency ?? "NAD"),
        maxTotalUses: d.maxTotalUses == null ? "" : String(d.maxTotalUses),
        maxUsesPerCustomer: d.maxUsesPerCustomer == null ? "" : String(d.maxUsesPerCustomer),
        validFrom: toLocalInputValue(d.validFrom ?? null),
        validUntil: toLocalInputValue(d.validUntil ?? null),
        minOrderAmount: d.minOrderAmount == null ? "" : String(d.minOrderAmount),
        ticketTypeIds: Array.isArray(d.ticketTypeIds) ? d.ticketTypeIds.map((x) => String(x)) : [],
        description: String(d.description ?? ""),
    };
}

export function CouponsStep({mode}: { mode: "create" | "edit" }) {
    const {activeBusiness} = useBusiness();
    const businessId = activeBusiness?.id ?? null;

    const navigate = useNavigate();
    const params = useParams();
    const [sp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const eventId =
        mode === "edit"
            ? (params.eventId ?? outlet.eventId ?? null)
            : (outlet.eventId ?? sp.get("eventId") ?? null);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [coupons, setCoupons] = useState<CouponDto[]>([]);
    const [ticketOptions, setTicketOptions] = useState<TicketOption[]>([]);

    // modal state
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState<CouponForm>(emptyForm());

    const load = async () => {
        if (!businessId || !eventId) return;

        setLoading(true);
        try {
            const {data} = await api.get(`/api/v1/business/${businessId}/events/${eventId}/coupons`);
            const list: CouponDto[] = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
            setCoupons(list);
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to load coupons"));
        } finally {
            setLoading(false);
        }

        try {
            const tRes = await api.get(`/api/v1/business/${businessId}/events/${eventId}/tickets`);
            const raw = Array.isArray(tRes?.data) ? tRes.data : (tRes?.data?.tickets ?? []);
            setTicketOptions(
                (raw ?? [])
                    .slice()
                    .sort((a: any, b: any) => (a?.position ?? 0) - (b?.position ?? 0))
                    .map((t: any) => ({
                        id: String(t?.id ?? ""),
                        name: t?.name ?? "Untitled",
                        kind: (t?.kind ?? "TICKET") as TicketKind,
                    }))
                    .filter((t: TicketOption) => t.id)
            );
        } catch {
            setTicketOptions([]);
        }
    };

    useEffect(() => {
        if (!businessId || !eventId) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId, eventId]);

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm());
        setOpen(true);
    };

    const openEdit = (c: CouponDto) => {
        setEditingId(c.id);
        setForm(formFromDto(c));
        setOpen(true);
    };

    const closeModal = () => {
        setOpen(false);
        setEditingId(null);
        setForm(emptyForm());
    };

    const codeNorm = useMemo(() => normalizeCode(form.code), [form.code]);

    const discountValueNum = useMemo(() => {
        const v = toNumber(form.discountValue);
        if (form.discountType === "PERCENT") return Math.min(100, Math.max(0, v));
        return Math.max(0, v);
    }, [form.discountValue, form.discountType]);

    const maxTotalUsesNum = useMemo(() => toIntOrNull(form.maxTotalUses), [form.maxTotalUses]);
    const maxUsesPerCustomerNum = useMemo(() => toIntOrNull(form.maxUsesPerCustomer), [form.maxUsesPerCustomer]);

    const minOrderAmountNum = useMemo(() => {
        const s = (form.minOrderAmount ?? "").trim();
        if (!s) return null;
        return Math.max(0, toNumber(s));
    }, [form.minOrderAmount]);

    const validation = useMemo(() => {
        if (!form.enabled) return {ok: true, msg: ""};

        if (!codeNorm) return {ok: false, msg: "Coupon code is required."};

        // unique code check (case-insensitive) against existing coupons
        const clash = coupons.find((c) => normalizeCode(c.code) === codeNorm && c.id !== editingId);
        if (clash) return {ok: false, msg: `Code "${codeNorm}" already exists for this event.`};

        if (form.discountType === "PERCENT") {
            if (discountValueNum <= 0) return {ok: false, msg: "Percentage discount must be greater than 0."};
        } else {
            if (discountValueNum <= 0) return {ok: false, msg: "Fixed discount must be greater than 0."};
        }

        if (maxTotalUsesNum != null && maxUsesPerCustomerNum != null && maxUsesPerCustomerNum > maxTotalUsesNum) {
            return {ok: false, msg: "Max uses per customer cannot exceed max total uses."};
        }

        const vf = fromLocalInputValue(form.validFrom);
        const vu = fromLocalInputValue(form.validUntil);
        if (vf && vu && new Date(vf).getTime() > new Date(vu).getTime()) {
            return {ok: false, msg: "Valid until must be after valid from."};
        }

        return {ok: true, msg: ""};
    }, [
        form.enabled,
        codeNorm,
        coupons,
        editingId,
        form.discountType,
        discountValueNum,
        maxTotalUsesNum,
        maxUsesPerCustomerNum,
        form.validFrom,
        form.validUntil,
    ]);

    const saveCoupon = async () => {
        if (!businessId || !eventId) return;

        if (!validation.ok) {
            notify.error(validation.msg || "Please fix the form errors.");
            return;
        }

        setSaving(true);
        try {
            const payload = {
                enabled: form.enabled,
                code: codeNorm,

                discountType: form.enabled ? form.discountType : null,
                discountValue: form.enabled ? discountValueNum : 0,
                currency: form.currency ?? "NAD",

                // null => unlimited (recommended)
                maxTotalUses: form.enabled ? maxTotalUsesNum : null,
                maxUsesPerCustomer: form.enabled ? maxUsesPerCustomerNum : null,

                validFrom: form.enabled ? fromLocalInputValue(form.validFrom) : null,
                validUntil: form.enabled ? fromLocalInputValue(form.validUntil) : null,

                minOrderAmount: form.enabled ? minOrderAmountNum : null,

                ticketTypeIds: form.enabled ? form.ticketTypeIds : [],
                description: form.description ?? "",
            };

            if (editingId) {
                await api.put(
                    `/api/v1/business/${businessId}/events/${eventId}/coupons/${encodeURIComponent(editingId)}`,
                    payload
                );
                notify.success("Coupon updated");
            } else {
                await api.post(`/api/v1/business/${businessId}/events/${eventId}/coupons`, payload);
                notify.success("Coupon created");
            }

            closeModal();
            await load();
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to save coupon"));
        } finally {
            setSaving(false);
        }
    };

    const toggleEnabled = async (c: CouponDto) => {
        if (!businessId || !eventId) return;

        setSaving(true);
        try {
            await api.put(`/api/v1/business/${businessId}/events/${eventId}/coupons/${encodeURIComponent(c.id)}`, {
                enabled: !c.enabled,
            });
            // optimistic refresh
            setCoupons((prev) => prev.map((x) => (x.id === c.id ? {...x, enabled: !c.enabled} : x)));
            notify.success(`Coupon ${!c.enabled ? "enabled" : "disabled"}`);
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to update coupon"));
        } finally {
            setSaving(false);
        }
    };

    const deleteCoupon = async (c: CouponDto) => {
        if (!businessId || !eventId) return;

        const used = Number(c.usedCount ?? 0);
        const msg =
            used > 0
                ? `This coupon was already used ${used} time(s). Deleting it may affect reporting. Continue?`
                : `Delete coupon "${c.code}"?`;

        // eslint-disable-next-line no-alert
        if (!window.confirm(msg)) return;

        setSaving(true);
        try {
            await api.delete(`/api/v1/business/${businessId}/events/${eventId}/coupons/${encodeURIComponent(c.id)}`);
            setCoupons((prev) => prev.filter((x) => x.id !== c.id));
            notify.success("Coupon deleted");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to delete coupon"));
        } finally {
            setSaving(false);
        }
    };

    const sortedCoupons = useMemo(() => {
        const score = (c: CouponDto) => {
            // active first, then enabled, then code
            const exp = isExpired(c) ? 1 : 0;
            const nys = isNotYetActive(c) ? 1 : 0;
            const active = exp === 0 && nys === 0 ? 0 : 1;
            return [active, c.enabled ? 0 : 1, normalizeCode(c.code)];
        };
        return [...coupons].sort((a, b) => {
            const sa = score(a);
            const sb = score(b);
            for (let i = 0; i < sa.length; i++) {
                if (sa[i] < sb[i]) return -1;
                if (sa[i] > sb[i]) return 1;
            }
            return 0;
        });
    }, [coupons]);

    const goNext = () => {
        if (!eventId) return;
        if (mode === "edit") navigate(`/business/events/${encodeURIComponent(eventId)}/edit/promoters`);
        else navigate(`/business/events/new/promoters?eventId=${encodeURIComponent(eventId)}`);
    };

    if (!eventId) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                Please complete <span className="font-medium">Event Details</span> first.
            </div>
        );
    }

    return (
        <div className="space-y-6">

            <SectionHeading
                title={mode === "edit" ? "Manage Coupons" : "Create Coupons"}
                description={mode === "edit" ? "Edit existing coupons or create new ones for this event." : "Create coupon codes for this event (percentage or fixed discount), limit usages, and define a validity window."}
                actions={
                    <>
                        <Button type="button" variant="primary" size="md" onClick={() => openCreate()}
                                disabled={saving}>
                            <PlusIcon className="size-4"/>
                            New coupon
                        </Button>
                    </>
                }
            />

            {loading ? (
                <div className="py-10 text-sm text-gray-500">Loading coupons…</div>
            ) : (
                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <div className="border-b border-gray-200 p-6">
                        <div className="text-sm font-semibold text-gray-900">Existing coupons</div>
                        <p className="mt-1 text-sm text-gray-600">
                            {sortedCoupons.length ? `${sortedCoupons.length} coupon(s)` : "No coupons yet."}
                        </p>
                    </div>

                    {sortedCoupons.length ? (
                        <div className="divide-y divide-gray-100">
                            {sortedCoupons.map((c) => {
                                const expired = isExpired(c);
                                const notYet = isNotYetActive(c);
                                const used = Number(c.usedCount ?? 0);
                                const max = c.maxTotalUses == null ? null : Number(c.maxTotalUses);
                                const usageLabel = max == null ? `${used} used` : `${used} / ${max} used`;

                                const statusPill = expired
                                    ? {t: "Expired", cls: "bg-gray-100 text-gray-700"}
                                    : notYet
                                        ? {t: "Scheduled", cls: "bg-blue-50 text-blue-700"}
                                        : c.enabled
                                            ? {t: "Active", cls: "bg-green-50 text-green-700"}
                                            : {t: "Disabled", cls: "bg-amber-50 text-amber-800"};

                                return (
                                    <div key={c.id} className="p-6">
                                        <div
                                            className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                            <div className="min-w-0">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <div
                                                        className="text-lg font-semibold text-gray-900">{normalizeCode(c.code)}</div>
                                                    <span
                                                        className={cx("rounded-full px-2.5 py-1 text-xs font-semibold", statusPill.cls)}>
                            {statusPill.t}
                          </span>
                                                </div>

                                                <div className="mt-1 text-sm text-gray-600">
                                                    <span className="font-medium">{formatDiscount(c)}</span>
                                                    <span className="mx-2 text-gray-300">•</span>
                                                    <span>{usageLabel}</span>
                                                    {c.maxUsesPerCustomer != null ? (
                                                        <>
                                                            <span className="mx-2 text-gray-300">•</span>
                                                            <span>Per customer: {c.maxUsesPerCustomer}</span>
                                                        </>
                                                    ) : null}
                                                </div>

                                                <div
                                                    className="mt-2 text-xs text-gray-500">{formatWindow(c)}</div>

                                                {c.description ? (
                                                    <div
                                                        className="mt-3 text-sm text-gray-700">{c.description}</div>
                                                ) : null}

                                                <div className="mt-3 text-xs text-gray-500">
                                                    Applies to:{" "}
                                                    {Array.isArray(c.ticketTypeIds) && c.ticketTypeIds.length
                                                        ? `${c.ticketTypeIds.length} ticket type(s)`
                                                        : "All ticket types"}
                                                    {c.minOrderAmount != null && Number(c.minOrderAmount) > 0 ? (
                                                        <>
                                                            <span className="mx-2 text-gray-300">•</span>
                                                            <span>Min order: N${Number(c.minOrderAmount).toFixed(2)}</span>
                                                        </>
                                                    ) : null}
                                                </div>
                                            </div>

                                            <div className="flex shrink-0 flex-col gap-2 sm:items-end">
                                                <label className="inline-flex items-center gap-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={Boolean(c.enabled)}
                                                        onChange={() => toggleEnabled(c)}
                                                        disabled={saving || expired}
                                                        className="size-5 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                                                    />
                                                    <span
                                                        className={cx("text-sm font-semibold", c.enabled ? "text-gray-900" : "text-gray-500")}>
                            {c.enabled ? "Enabled" : "Disabled"}
                          </span>
                                                </label>

                                                <div className="flex gap-2">
                                                    <Button type="button" variant="secondary" size="md"
                                                            onClick={() => openEdit(c)} disabled={saving}>
                            <span className="inline-flex items-center gap-2">
                              <PencilIcon className="size-4"/>
                              Edit
                            </span>
                                                    </Button>
                                                    <Button type="button" variant="danger" size="md"
                                                            onClick={() => deleteCoupon(c)} disabled={saving}>
                            <span className="inline-flex items-center gap-2">
                              <Trash2Icon className="size-4"/>
                              Delete
                            </span>
                                                    </Button>
                                                </div>

                                                {expired ? (
                                                    <div className="text-xs text-gray-500">Expired coupons can’t
                                                        be
                                                        enabled.</div>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="p-6 text-sm text-gray-600">
                            No coupons created yet. Click <span className="font-medium">New coupon</span> to add
                            one.
                        </div>
                    )}
                </div>
            )}

            <div className="flex items-center justify-end gap-3">
                <Button type="button" variant="secondary" size="md"
                        onClick={() => navigate("/business/events")}>
                    Exit
                </Button>
                <Button type="button" variant="primary" size="md" onClick={goNext} disabled={saving}>
                    Continue to Promoters
                </Button>
            </div>

            {/* Modal */}
            {open ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/30" onClick={closeModal}/>
                    <div
                        className="relative flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col rounded-2xl border border-gray-200 bg-white shadow-xl">
                        <div className="flex shrink-0 items-start justify-between border-b border-gray-200 p-6">
                            <div>
                                <div className="text-xl font-semibold text-gray-900">
                                    {editingId ? "Edit coupon" : "New coupon"}
                                </div>
                                <p className="mt-1 text-sm text-gray-600">
                                    Configure code, discount, limits and validity window.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeModal}
                                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                            >
                                <XIcon className="size-5"/>
                            </button>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto p-6">
                            {!validation.ok && form.enabled ? (
                                <div
                                    className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                                    {validation.msg}
                                </div>
                            ) : null}

                            <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-gray-900">Enable coupon</div>
                                    <p className="mt-1 text-sm text-gray-600">If disabled, the code can’t be
                                        applied at
                                        checkout.</p>
                                </div>
                                <label className="inline-flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        checked={form.enabled}
                                        onChange={(e) => setForm((p) => ({...p, enabled: e.target.checked}))}
                                        className="size-5 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                                    />
                                    <span
                                        className={cx("text-sm font-semibold", form.enabled ? "text-gray-900" : "text-gray-500")}>
                    {form.enabled ? "Enabled" : "Disabled"}
                  </span>
                                </label>
                            </div>

                            <div
                                className={cx("mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2", !form.enabled && "opacity-50")}>
                                <div>
                                    <label className="block text-sm font-medium text-gray-900">Coupon
                                        code</label>
                                    <p className="mt-1 text-xs text-gray-500">Unique per event (A–Z, 0–9, - ,
                                        _)</p>
                                    <input
                                        disabled={!form.enabled}
                                        value={form.code}
                                        onChange={(e) => setForm((p) => ({...p, code: e.target.value}))}
                                        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                    />
                                    <p className="mt-2 text-xs text-gray-500">
                                        Normalized: <span className="font-medium">{codeNorm || "—"}</span>
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-900">Discount
                                        type</label>
                                    <p className="mt-1 text-xs text-gray-500">Percentage or fixed amount</p>
                                    <select
                                        disabled={!form.enabled}
                                        value={form.discountType}
                                        onChange={(e) => setForm((p) => ({
                                            ...p,
                                            discountType: e.target.value as DiscountType
                                        }))}
                                        className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                    >
                                        <option value="PERCENT">Percentage (%)</option>
                                        <option value="FIXED">Fixed value</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-900">
                                        Discount value {form.discountType === "PERCENT" ? "(%)" : "(amount)"}
                                    </label>
                                    <p className="mt-1 text-xs text-gray-500">
                                        {form.discountType === "PERCENT" ? "Example: 10 = 10% off" : "Example: 50 = N$50 off"}
                                    </p>
                                    <input
                                        disabled={!form.enabled}
                                        value={form.discountValue}
                                        onChange={(e) => setForm((p) => ({
                                            ...p,
                                            discountValue: e.target.value
                                        }))}
                                        inputMode="decimal"
                                        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                    />
                                    <p className="mt-2 text-xs text-gray-500">
                                        Current:{" "}
                                        <span className="font-medium">
                      {form.discountType === "PERCENT"
                          ? `${discountValueNum.toFixed(0)}%`
                          : `N$${discountValueNum.toFixed(2)}`}
                    </span>
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-900">Min order amount
                                        (optional)</label>
                                    <p className="mt-1 text-xs text-gray-500">Only apply coupon if cart total
                                        meets this
                                        amount</p>
                                    <input
                                        disabled={!form.enabled}
                                        value={form.minOrderAmount}
                                        onChange={(e) => setForm((p) => ({
                                            ...p,
                                            minOrderAmount: e.target.value
                                        }))}
                                        inputMode="decimal"
                                        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-900">Max total uses
                                        (blank =
                                        unlimited)</label>
                                    <p className="mt-1 text-xs text-gray-500">How many times the code can be
                                        used
                                        overall</p>
                                    <input
                                        disabled={!form.enabled}
                                        value={form.maxTotalUses}
                                        onChange={(e) => setForm((p) => ({...p, maxTotalUses: e.target.value}))}
                                        inputMode="numeric"
                                        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-900">Max uses per
                                        customer
                                        (blank = unlimited)</label>
                                    <p className="mt-1 text-xs text-gray-500">Limit per account/customer</p>
                                    <input
                                        disabled={!form.enabled}
                                        value={form.maxUsesPerCustomer}
                                        onChange={(e) => setForm((p) => ({
                                            ...p,
                                            maxUsesPerCustomer: e.target.value
                                        }))}
                                        inputMode="numeric"
                                        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-900">Valid from
                                        (optional)</label>
                                    <input
                                        disabled={!form.enabled}
                                        type="datetime-local"
                                        value={form.validFrom}
                                        onChange={(e) => setForm((p) => ({...p, validFrom: e.target.value}))}
                                        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-900">Valid until
                                        (optional)</label>
                                    <input
                                        disabled={!form.enabled}
                                        type="datetime-local"
                                        value={form.validUntil}
                                        onChange={(e) => setForm((p) => ({...p, validUntil: e.target.value}))}
                                        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                    />
                                </div>

                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-gray-900">Apply to ticket
                                        types</label>
                                    <p className="mt-1 text-xs text-gray-500">
                                        Leave "Applies to all tickets" on, or turn it off to restrict this coupon
                                        to specific ticket types.
                                    </p>
                                    <TicketScopePicker
                                        label="Apply this coupon"
                                        ticketTypeIds={form.ticketTypeIds}
                                        tickets={ticketOptions}
                                        onChange={(ids) => setForm((p) => ({...p, ticketTypeIds: ids}))}
                                    />
                                </div>

                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-gray-900">Customer message
                                        (optional)</label>
                                    <p className="mt-1 text-xs text-gray-500">Shown near coupon field at
                                        checkout</p>
                                    <textarea
                                        disabled={!form.enabled}
                                        rows={4}
                                        value={form.description}
                                        onChange={(e) => setForm((p) => ({...p, description: e.target.value}))}
                                        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex shrink-0 items-center justify-between border-t border-gray-200 p-6">
                            <Button type="button" variant="secondary" size="md" onClick={closeModal}
                                    disabled={saving}>
                                Cancel
                            </Button>
                            <Button type="button" variant="primary" size="md" onClick={saveCoupon}
                                    disabled={saving}>
                                <span className="inline-flex items-center gap-2">
                                  <SaveIcon className="size-4"/>
                                    {editingId ? "Save changes" : "Create coupon"}
                                </span>
                            </Button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}